import React from "react";
import {
  Box,
  Flex,
  IconButton,
  ButtonGroup,
  Avatar,
  Skeleton,
  Tabs,
  TabPanel,
  TabPanels,
  useTab,
} from "@chakra-ui/react";
import { Large, P } from "../../components/Type";
import { Dialog } from "../../components/Dialog";
import Head from "../../components/Head";
import { Footer } from "../../components/Footer";
import Button from "../../components/Button";
import { Announcement } from "../../components/Announcement";
import {
  FaTwitch,
  FaYoutube,
  FaInstagram,
  FaFacebookF,
  FaTwitter,
  FaTiktok,
  FaCheck,
} from "react-icons/fa";
import { SiKick } from "react-icons/si";
import { useRouter } from "next/router";
import Image from "next/image";
import { RacoonSad, DefaultCharacter, DoggoError } from "../../assets";
import { LoginHelperModal } from "./LoginHelper";
import { useUserInfo } from "../../hooks/useUserInfo";
import { Profile } from "../../components/AppShell";
import dynamic from "next/dynamic";
import { msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";

const SingleCardPage = dynamic(
  () =>
    import("../../components/SingleCardPage").then((mod) => mod.SingleCardPage),
  { ssr: false }
);

const socialIcon = {
  facebook: <FaFacebookF />,
  instagram: <FaInstagram />,
  twitch: <FaTwitch />,
  twitter: <FaTwitter />,
  youtube: <FaYoutube />,
  tiktok: <FaTiktok />,
  kick: <SiKick />,
};

const getSocialUrl = (platform, handle) => {
  return `https://${platform}.com/${handle}`;
  
  // switch (platform) {
  //   case "tiktok":
  //     return `https://tiktok.com/${handle}`;
  //   case "kick":
  //     return `https://kick.com/${handle}`;
  //   default:
  //     return `https://${platform}.com/${handle}`;
  // }
};

const SocialLinks = ({ socials }) => (
  <Flex justify={["center", "left"]} mb={3}>
    <ButtonGroup spacing={0}>
      {Object.keys(socials)
        .filter((s) => socials[s])
        .map((s, i) => (
          <IconButton
            key={i}
            as="a"
            target="_blank"
            href={getSocialUrl(s, socials[s])}
            isRound
            size="md"
            variant="ghost"
            colorScheme="gray"
            aria-label={s}
            fontSize="25px"
            icon={socialIcon[s]}
          />
        ))}
    </ButtonGroup>
  </Flex>
);

const ErrorMessage = ({ error }) => {
  const { status: errorStatusCode, json: response = {} } = error;
  const { _ } = useLingui();

  if (errorStatusCode) {
    const { message } = response;
    let label = msg`Hmm ada yang salah dengan layanan kami`;
    let title = "Uh Oh";
    let image = RacoonSad;

    switch (message) {
      case "NOT_ACTIVATED":
        title = msg`User belum aktif`;
        label = msg`User belum aktif`;
        break;
      case "USER_SUSPENDED":
        title = "SUSPENDED";
        label = msg`User telah melanggar Syarat dan Ketentuan`;
        break;
      case "USER_BANNED":
        title = "BANNED";
        label = msg`User telah melanggar Syarat dan Ketentuan`;
        break;
      case "User does not exist":
        title = msg`User tidak ditemukan`;
        label = msg`User tidak ditemukan`;
        image = DoggoError;
        break;
    }

    return (
      <SingleCardPage
        backgroundColor="saweria-purple.light"
        imageSrc={image}
        message={_(label)}
        title={_(title)}
      />
    );
  }
};

const CustomTab = React.forwardRef((props, ref) => {
  const tabProps = useTab({ ...props, ref });
  const isSelected = !!tabProps["aria-selected"];
  const { caption } = tabProps;

  const buttonProps = isSelected
    ? {
        boxShadow: "pressed",
        backgroundColor: "saweria-yellow.normal",
        variant: "solid",
      }
    : {
        backgroundColor: "saweria-gray.normal",
        variant: "outline",
      };

  return (
    <Button
      {...buttonProps}
      type="button"
      {...tabProps}
      label={caption}
      fontSize={{ base: "md", md: "lg" }}
      padding={{ base: "0.4em 0.35em", md: "0.45em 0.65em" }}
      w={"full"}
    />
  );
});

/**
 * Resolves whether game vouchers are enabled for this streamer.
 */
export function resolveGameVoucherEnabled(username, overlayFlag) {
  const globalEnabled = process.env.NEXT_PUBLIC_GAME_VOUCHERS_ENABLED === "true";
  const usernameEnabled = process.env.NEXT_PUBLIC_GAME_VOUCHERS_USERNAME_WHITELIST_ENABLED === "true";
  const allowedUsernames = (process.env.NEXT_PUBLIC_GAME_VOUCHERS_ALLOWED_USERNAMES || "")
    .split(",")
    .map((u) => u.trim())
    .filter(Boolean);
  return overlayFlag && ((globalEnabled && !usernameEnabled) || (globalEnabled && usernameEnabled && allowedUsernames.includes(username)));
}

/**
 * Resolves whether Mini Games are shown for this streamer: the streamer's own switch
 * (overlay.mini_games_enabled, user API) + env kill switch / optional username whitelist.
 */
export function resolveMiniGamesEnabled(username, overlayFlag) {
  const globalEnabled = process.env.NEXT_PUBLIC_MINI_GAMES_ENABLED === "true";
  const usernameEnabled = process.env.NEXT_PUBLIC_MINI_GAMES_USERNAME_WHITELIST_ENABLED === "true";
  const allowedUsernames = (process.env.NEXT_PUBLIC_MINI_GAMES_ALLOWED_USERNAMES || "")
    .split(",")
    .map((u) => u.trim())
    .filter(Boolean);
  return Boolean(overlayFlag && globalEnabled && (!usernameEnabled || allowedUsernames.includes(username)));
}

/**
 * Builds tab captions and routes based on streamer features and tier.
 */
export function useStreamerTabs(data) {
  const {
    username,
    overlay = {},
    has_wishlist: hasWishlist,
    tier_key,
    has_store: hasStore,
  } = data;

  const { soundboard } = overlay;
  const gameVoucherEnabled = resolveGameVoucherEnabled(username, overlay.game_voucher_enabled);
  const miniGamesEnabled = resolveMiniGamesEnabled(username, overlay.mini_games_enabled);

  const isTopUp = tier_key === "TOPUP";

  const tabCaptions = isTopUp
    ? [msg`Top Up`]
    : [
        msg`Dukungan`,
        ...(gameVoucherEnabled ? [msg`Top Up`] : []),
        ...(hasStore ? [msg`Toko`] : []),
        // right after Toko (each [username] page pushes its panel in this order)
        ...(miniGamesEnabled ? [msg`Mini Games`] : []),
        ...(hasWishlist ? [msg`Wishlist`] : []),
        ...(soundboard ? [msg`Soundboard`] : []),
      ];

  const tabRoutes = isTopUp
    ? [`/${username}/toko-top-up`]
    : [
        `/${username}`,
        ...(gameVoucherEnabled ? [`/${username}/toko-top-up`] : []),
        ...(hasStore ? [`/${username}/toko`] : []),
        ...(miniGamesEnabled ? [`/${username}/mini-games`] : []),
        ...(hasWishlist ? [`/${username}/wishlist`] : []),
        ...(soundboard ? [`/${username}/soundboard`] : []),
      ];

  return { tabCaptions, tabRoutes, isTopUp, gameVoucherEnabled, hasWishlist, soundboard, hasStore, miniGamesEnabled };
}

/**
 * Extracts common payment flags from data.
 */
export function getPaymentFlagsFromData(data) {
  return {
    isGopayEnabled: data.isGopayEnabled,
    isOvoEnabled: data.isOvoEnabled,
    isDanaEnabled: data.isDanaEnabled,
    isLinkAjaEnabled: data.isLinkAjaEnabled,
    isQRISEnabled: data.isQRISEnabled,
    isBCAVAEnabled: data.isBCAVAEnabled,
    isBNIVAEnabled: data.isBNIVAEnabled,
    isBRIVAEnabled: data.isBRIVAEnabled,
    isMandiriVAEnabled: data.isMandiriVAEnabled,
    isPermataVAEnabled: data.isPermataVAEnabled,
    isBJBVAEnabled: data.isBJBVAEnabled,
    isBSIVAEnabled: data.isBSIVAEnabled,
    isCIMBVAEnabled: data.isCIMBVAEnabled,
  };
}

/**
 * StreamerPageLayout - shared shell + tab navigation for all [username] pages.
 *
 * Props:
 * - data: streamer API data
 * - error: error object from getStaticProps
 * - head: optional <Head> content for the active page
 * - noDialog: if true, render tab panels without Dialog wrapper
 * - children: tab panel content(s) — array of React elements matching tab order
 */
export const StreamerPageLayout = ({ data = {}, error, head, noDialog = false, skipTabs = false, children }) => {
  const router = useRouter();
  const { isFallback } = router;
  const { data: userInfo = {} } = useUserInfo({}, () => {});
  const { username: currUsername } = userInfo;
  const [mode, setMode] = React.useState();
  const { _ } = useLingui();

  const { username, description, socials = [], profile_picture, verified } = data;
  const { tabCaptions, tabRoutes, isTopUp } = useStreamerTabs(data);
  const panels = children;

  const currentPath = (router.asPath || "").split("?")[0];
  const isTopUpPathCorrect = !isTopUp || !username || currentPath.startsWith(`/${username}/toko-top-up`);

  const [tabIndex, setTabIndex] = React.useState(isTopUp ? 0 : 0);

  // TOPUP redirect
  React.useEffect(() => {
    if (isTopUp && username && !currentPath.startsWith(`/${username}/toko-top-up`)) {
      router.replace(`/${username}/toko-top-up`);
    }
  }, [isTopUp, username, currentPath]);

  // Sync tab index with current route (only on initial load / external navigation)
  const isUserTabChange = React.useRef(false);
  React.useEffect(() => {
    if (!router.isReady) return;
    if (isUserTabChange.current) {
      isUserTabChange.current = false;
      return;
    }
    let path = (router.asPath || "").split("?")[0];

    // Normalize sub-routes to parent for tab matching
    const parts = path.split("/").filter(Boolean);
    if (parts.length >= 3 && parts[1] === "toko-top-up") {
      path = `/${parts[0]}/toko-top-up`;
    }
    if (parts.length >= 3 && parts[1] === "wishlist") {
      path = `/${parts[0]}/wishlist`;
    }
    if (parts.length >= 3 && parts[1] === "toko") {
      path = `/${parts[0]}/toko`;
    }
    if (parts.length >= 2 && parts[1] === "mini-games") {
      path = `/${parts[0]}/mini-games`;
    }

    const idx = tabRoutes.indexOf(path);
    if (idx >= 0) setTabIndex(idx);
  }, [router.isReady, router.asPath, tabRoutes]);

  if (error) {
    return <ErrorMessage error={error} />;
  }

  if (!isTopUpPathCorrect) {
    return null;
  }

  return (
    <Box overflowX="clip">
      <Head title={`Memproses`} />

      {!isFallback ? <Announcement /> : null}
      <Flex direction="column" alignItems="center" minH="100vh">
        <Flex
          flexGrow={1}
          w={["100%", "100%", "720px"]}
          maxW="720px"
          paddingRight={["3", "3", 0]}
          paddingLeft={["1", "3", 0]}
          direction="column"
          justifyContent="center"
        >
          <Flex direction="row" justifyContent="end">
            {currUsername ? (
              <Profile
                profilePicture={userInfo?.profile_picture}
                username={userInfo?.username}
              />
            ) : (
              <Button
                disabled={isFallback}
                bgColor="saweria-green.normal"
                label={_(msg`Login`)}
                onClick={() => setMode("login")}
              />
            )}
          </Flex>

          {!isFallback ? (
            <LoginHelperModal mode={mode} setMode={setMode} userInfo={userInfo} />
          ) : null}

          {/* Avatar + Info */}
          <Flex direction={["column", "row", "row"]} alignItems="center" mb={3}>
            {profile_picture ? (
              <Avatar
                w={["120px", "100px", "150px"]}
                h={["120px", "100px", "150px"]}
                size="2xl"
                m={5}
                name={username?.toUpperCase()}
                src={profile_picture}
              />
            ) : (
              <Box w={["50%", "30%"]} flexShrink="0" mb={[3, 0]} mt={3} mr={[0, 3]}>
                <Image alt="Saweria Logo" src={DefaultCharacter} />
              </Box>
            )}
            <Box textAlign={["center", "left"]} w="100%">
              {isFallback ? (
                <Skeleton height={"20px"} w="100%" />
              ) : (
                <Large>
                  <span style={{ whiteSpace: "nowrap" }}>
                    {username}
                    {verified ? (
                      <IconButton
                        title="Verified"
                        ml={2}
                        size="xs"
                        variant="solid"
                        colorScheme="blue"
                        aria-label="Verified"
                        icon={<FaCheck />}
                      />
                    ) : null}
                  </span>
                </Large>
              )}
              <SocialLinks socials={socials} />
              <P>{description}</P>
            </Box>
          </Flex>

          {isFallback ? (
            <Flex
              h="50vh"
              alignItems="center"
              justifyContent="space-evenly"
              direction="column"
            >
              <Skeleton h="20px" my="1em" w="100%" />
              <Skeleton h="20px" my="1em" w="100%" />
              <Skeleton h="20px" my="1em" w="100%" />
              <Skeleton h="20px" my="1em" w="100%" />
              <Skeleton h="20px" my="1em" w="100%" />
              <Skeleton h="20px" my="1em" w="100%" />
            </Flex>
          ) : (
            <>
              {head}

              {skipTabs ? (
                children
              ) : (
                <Tabs
                  index={tabIndex}
                  onChange={(index) => {
                    isUserTabChange.current = true;
                    setTabIndex(index);
                    router.replace(tabRoutes[index], undefined, { shallow: true });
                  }}
                  variant="soft-rounded"
                  isLazy
                  lazyBehavior="keepMounted"
                  p={{ base: 3, md: 0 }}
                >
                  {/* many tabs scroll sideways; a visible scrollbar would push the tabs off the card */}
                  <Flex
                    mb={-4}
                    position="relative"
                    whiteSpace="nowrap"
                    overflowX="auto"
                    sx={{ scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}
                  >
                    {tabCaptions.map((caption, index) => (
                      <Box
                        ml={index > 0 ? 3 : 0}
                        key={index}
                        flex={{ base: 1, md: 0 }}
                        whiteSpace="nowrap"
                      >
                        <CustomTab caption={_(caption)} w="100%" />
                      </Box>
                    ))}
                  </Flex>

                  {noDialog ? (
                    <TabPanels>
                      {React.Children.map(panels, (child) => (
                        <TabPanel p="0px!important">{child}</TabPanel>
                      ))}
                    </TabPanels>
                  ) : (
                    <Dialog
                      marginBottom={3}
                      minHeight="60vh"
                      zIndex={1}
                      position="relative"
                      borderTopLeftRadius="0"
                    >
                      <TabPanels>
                        {React.Children.map(panels, (child) => (
                          <TabPanel p="0px!important">{child}</TabPanel>
                        ))}
                      </TabPanels>
                    </Dialog>
                  )}
                </Tabs>
              )}
            </>
          )}

          <Footer marginTop={4} />
        </Flex>
      </Flex>
    </Box>
  );
};

export default StreamerPageLayout;
